import {User} from '../types/manageUsers';


/**
 * Validates an email string.
 */
export const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  const trimmed = email.trim();
  if (!trimmed) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmed);
};

export const sanitizeUserId = (userId: string): string => {
  if (userId.startsWith('jira_')) {
    return userId.replace('jira_', '');
  }
  if (userId.startsWith('azure_')) {
    return userId.replace('azure_', '');
  }
  return userId;
};

/**
 * Adds integration prefix back to sanitized ID
 */
export const addIntegrationPrefix = (sanitizedId: string, isJira: boolean): string => {
  const prefix = isJira ? 'jira_' : 'azure_';
  return `${prefix}${sanitizedId}`;
};

/**
 * Converts users to CSV format for download
 * If a user's id is included in editableUserIds, the email will be left empty in the CSV.
 */
export const usersToCSV = (users: User[], editableUserIds: string[] = [], overrides: Record<string, string> = {}): string => {
  const editable = new Set(editableUserIds);
  const headers = ['id', 'name', 'email'];
  const csvContent = [
    headers.join(','),
    ...users.map(user => {
      const sanitizedId = sanitizeUserId(user.id);
      const name = `"${user.display_name}"`;
      const overrideEmail = (overrides[user.id] || '').trim();
      const emailValue = overrideEmail
        ? overrideEmail
        : (editable.has(user.id) ? '' : (user.email || ''));
      const email = `"${emailValue}"`;
      return [sanitizedId, name, email].join(',');
    })
  ].join('\n');
  
  return csvContent;
};

/**
 * Parses CSV content and extracts email mappings
 */
export interface ParseCSVResult {
  emails: Record<string, string>;
  errors: string[];
}

/**
 * Parses CSV content and extracts email mappings while enforcing immutability
 * of non-editable users and other details like id and name.
 */
export const parseCSVEmails = (
  csvContent: string,
  isJira: boolean,
  originalUsers: User[],
  editableUserIds: string[]
): ParseCSVResult => {
  const lines = csvContent.split('\n').filter(line => line.trim());
  const emails: Record<string, string> = {};
  const errors: string[] = [];

  const idToUser: Record<string, User> = Object.fromEntries(originalUsers.map(u => [u.id, u]));
  const editable = new Set(editableUserIds);

  // Skip header row and process data lines
  const dataLines = lines.slice(1);
  for (const line of dataLines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;
    const values = parseCSVLine(trimmedLine);
    if (values.length < 3) continue;

    const sanitizedId = values[0].replace(/(^"|"$)/g, '').trim();
    const _name = values[1].replace(/(^"|"$)/g, '').trim();
    const email = values[2].replace(/(^"|"$)/g, '').trim();

    const fullId = addIntegrationPrefix(sanitizedId, isJira);
    const original = idToUser[fullId];
    if (!original) {
      continue;
    }

    

    const isEditable = editable.has(fullId);
    if (!isEditable) {
      continue;
    }

    if (email) {
      emails[fullId] = email;
    }
  }

  if (errors.length > 0) {
    return { emails: {}, errors };
  }
  return { emails, errors };
};

/**
 * Parses a single CSV line handling quoted values
 */
const parseCSVLine = (line: string): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (const char of line) {
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  values.push(current.trim());
  return values;
};

/**
 * Downloads CSV file
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Reads file as text
 */
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      resolve(text);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};
